import React, { useState, FC, useRef, RefObject, useEffect } from 'react';
import {
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Paper,
    IconButton,
    CircularProgress,
    Alert,
    AlertTitle,
    Box,
    Grid,
    TextField,
    InputAdornment,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { TimekeeperService } from 'services/TimekeeperService';
import { Paycode } from 'repository/timekeeper/@types/PaycodeModel';
import { DatePattern } from 'repository/timekeeper/@types/DatePattern';
import { GrantTypeEnum } from 'repository/timekeeper/@types/TypeGrant';
import { GrantConcessionEnum } from 'repository/timekeeper/@types/TypeGrantConcession';
import { TipoEquivalenciaEnum } from 'repository/timekeeper/@types/TypeEquivalentia';
import { TipoImpactoCambio } from 'repository/timekeeper/@types/TypeChangeImpact';
import { FrequencyUnitTime } from 'repository/timekeeper/@types/TypeFrequencyUnitTime';
import { ApplicationEnum } from 'repository/timekeeper/@types/TypeAplicationEnum';
import { TipoCalificadorEnum } from 'repository/timekeeper/@types/TypeCalificador';
import { TipoOrtorgarConcesion } from 'repository/timekeeper/@types/TypeGiveGrant';
import { GrantFormFijo, GrantFormFijoData } from './GrantFormFijo';
import { GrantFormPercibido, GrantFormPercibidoData } from './GrantFormPercibido';
import { Grant } from 'repository/timekeeper/@types/Grant';
import { GrantFijoCreateSchema } from 'schemas/Grants';

interface CreateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: Grant[];
    reloading: () => void;
    cleanSelected: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable nodeRef={nodeRef as RefObject<HTMLDivElement>} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const timekeeperService = new TimekeeperService();

export const CreateGrant: FC<CreateProps> = ({ open, setOpen, rows, reloading, cleanSelected }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    // Estados básicos
    const [grantType, setGrantType] = useState<GrantTypeEnum>(GrantTypeEnum.FIJO);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    // Estados para tipo FIJO
    const [fijoFormData, setFijoFormData] = useState<GrantFormFijoData>({
        grantConcession: GrantConcessionEnum.REPETITIVO,
        datePatternId: '',
        expiration: false,
        expirationNumber: 1,
        expirationUnit: FrequencyUnitTime.DIAS,
        fullTimeEquivalents: TipoEquivalenciaEnum.NO_MODIFICA_CONCESION,
        paycodeEtcId: '',
        impactChangeType: TipoImpactoCambio.UTILIZAR_NORMA_ACUMULACION,
        impactRecalculate: false,
        qualifiers: [],
        apportionmentType: '',
    });

    // Estados para tipo PERCIBIDO
    const [percibidoFormData, setPercibidoFormData] = useState<GrantFormPercibidoData>({
        basedOn: TipoOrtorgarConcesion.INMEDIATAMENTE,
        paycodeId: '',
        datePatternId: '',
        applicationMode: ApplicationEnum.USAR_CANTIDAD_CONCESION,
        applicationModeAmount: 0,
        applicationModeMove: false,
        applicationModeDatePatternId: '',
        expiration: false,
        expirationNumber: 1,
        expirationUnit: FrequencyUnitTime.DIAS,
        qualifiers: [],
    });

    // Datos para selects
    const [availablePaycodes, setAvailablePaycodes] = useState<Paycode[]>([]);
    const [availableDatePatterns, setAvailableDatePatterns] = useState<DatePattern[]>([]);

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [alerts, setAlerts] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{
        name?: string;
        description?: string;
        expiration_number?: string;
    }>({});

    useEffect(() => {
        const loadData = async () => {
            setLoadingData(true);

            try {
                // TODO los paycodes a agregar a la lista de select, tambien agregar los codigos de pago combinados
                const [paycodes, datePatterns] = await Promise.all([timekeeperService.paycodes.getPaycodes(), timekeeperService.date_pattern.getAll()]);

                setAvailablePaycodes(paycodes as unknown as Paycode[]);
                setAvailableDatePatterns(datePatterns);
            } catch (error) {
                console.error('Error loading data:', error);
                enqueueSnackbar(t('accruals.grants.dialog.alerts.add.error'), { variant: 'error' });
            } finally {
                setLoadingData(false);
            }
        };

        if (open) {
            loadData();
        }
    }, [open, enqueueSnackbar, t]);

    const closeForm = () => {
        setGrantType(GrantTypeEnum.FIJO);
        setName('');
        setDescription('');
        setFijoFormData({
            grantConcession: GrantConcessionEnum.REPETITIVO,
            datePatternId: '',
            expiration: false,
            expirationNumber: 1,
            expirationUnit: FrequencyUnitTime.DIAS,
            fullTimeEquivalents: TipoEquivalenciaEnum.NO_MODIFICA_CONCESION,
            paycodeEtcId: '',
            impactChangeType: TipoImpactoCambio.UTILIZAR_NORMA_ACUMULACION,
            impactRecalculate: false,
            qualifiers: [],
            apportionmentType: '',
        });
        setPercibidoFormData({
            basedOn: TipoOrtorgarConcesion.INMEDIATAMENTE,
            paycodeId: '',
            datePatternId: '',
            applicationMode: ApplicationEnum.USAR_CANTIDAD_CONCESION,
            applicationModeAmount: 0,
            applicationModeMove: false,
            applicationModeDatePatternId: '',
            expiration: false,
            expirationNumber: 1,
            expirationUnit: FrequencyUnitTime.DIAS,
            qualifiers: [],
        });
        setOpen(false);
        setAlerts(false);
        setLoading(false);
        cleanSelected();
    };

    const changes = () => {
        if (name !== '') {
            setAlerts(true);
        } else {
            closeForm();
        }
    };

    const Create = async () => {
        setLoading(true);

        try {
            // Limpiar qualifier_grant_id de los qualifiers ya que es un campo de respuesta, no de creación
            const cleanQualifiers = (grantType === GrantTypeEnum.FIJO ? fijoFormData.qualifiers : percibidoFormData.qualifiers)
                .filter((q) => q.qualifier_type !== TipoCalificadorEnum.NINGUNO)
                .map((q) => {
                    const { qualifier_grant_id, ...qualifierWithoutId } = q as any;
                    return qualifierWithoutId;
                });

            let dataToCreate: any = {
                name,
                description: description || null,
                grant_type: grantType,
                qualifiers: cleanQualifiers,
            };

            if (grantType === GrantTypeEnum.FIJO) {
                dataToCreate = {
                    ...dataToCreate,
                    grant_concession: fijoFormData.grantConcession,
                    date_pattern_id: fijoFormData.datePatternId !== '' ? fijoFormData.datePatternId : null,
                    full_time_equivalents: fijoFormData.fullTimeEquivalents,
                    paycode_id: fijoFormData.fullTimeEquivalents === TipoEquivalenciaEnum.MODIFICA_CONCESION_PAYCODE && fijoFormData.paycodeEtcId !== '' ? fijoFormData.paycodeEtcId : null,
                    impact_change_type: fijoFormData.impactChangeType,
                    impact_recalculate: fijoFormData.impactRecalculate,
                    apportionment_type: fijoFormData.apportionmentType !== '' ? fijoFormData.apportionmentType : null,
                };

                dataToCreate.expiration = fijoFormData.expiration;
                dataToCreate.expiration_number = fijoFormData.expiration ? fijoFormData.expirationNumber : null;
                dataToCreate.expiration_unit_date = fijoFormData.expiration ? fijoFormData.expirationUnit : null;
            } else if (grantType === GrantTypeEnum.PERCIBIDO) {
                dataToCreate = {
                    ...dataToCreate,
                    paycode_id: percibidoFormData.paycodeId,
                    type_give_grant: percibidoFormData.basedOn,
                    date_pattern_id: percibidoFormData.basedOn === TipoOrtorgarConcesion.PATRON_DE_FECHAS && percibidoFormData.datePatternId !== '' ? percibidoFormData.datePatternId : null,
                    application_mode: percibidoFormData.basedOn === TipoOrtorgarConcesion.PATRON_DE_FECHAS ? percibidoFormData.applicationMode : null,
                    application_mode_amount:
                        percibidoFormData.basedOn === TipoOrtorgarConcesion.PATRON_DE_FECHAS && percibidoFormData.applicationMode === ApplicationEnum.ACOTAR_EN_INCREMENTO
                            ? percibidoFormData.applicationModeAmount
                            : null,
                    application_mode_move_reset:
                        percibidoFormData.basedOn === TipoOrtorgarConcesion.PATRON_DE_FECHAS && percibidoFormData.applicationMode === ApplicationEnum.ACOTAR_EN_INCREMENTO
                            ? percibidoFormData.applicationModeMove
                            : null,
                    application_mode_dates_pattern_id:
                        percibidoFormData.basedOn === TipoOrtorgarConcesion.PATRON_DE_FECHAS &&
                        percibidoFormData.applicationMode === ApplicationEnum.ACOTAR_EN_INCREMENTO &&
                        percibidoFormData.applicationModeMove &&
                        percibidoFormData.applicationModeDatePatternId !== ''
                            ? percibidoFormData.applicationModeDatePatternId
                            : null,
                };

                dataToCreate.expiration = percibidoFormData.expiration;
                dataToCreate.expiration_number = percibidoFormData.expiration ? percibidoFormData.expirationNumber : null;
                dataToCreate.expiration_unit_date = percibidoFormData.expiration ? percibidoFormData.expirationUnit : null;
            }

            console.log('Grant to create:', dataToCreate);
            const createdGrant = await timekeeperService.grants.create(dataToCreate);
            console.log('Created Grant:', createdGrant);
            enqueueSnackbar(t('accruals.grants.dialog.alerts.add.success'), { variant: 'success' });
            closeForm();
            reloading();
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('accruals.grants.dialog.alerts.add.error'), { variant: 'error' });
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes();
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="lg"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.grants.dialog.titles.add')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant="filled"
                        severity="warning"
                        sx={{ mt: 2, mx: 3 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                {loadingData ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
                            <TextField
                                fullWidth
                                label={t('accruals.grants.dialog.fields.name')}
                                value={name}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setName(value);
                                    // Validar nombre
                                    const result = GrantFijoCreateSchema.shape.name.safeParse(value);
                                    setValidationErrors((prev) => ({
                                        ...prev,
                                        name: result.success ? undefined : result.error.issues[0]?.message,
                                    }));
                                }}
                                required
                                autoComplete="off"
                                error={!!validationErrors.name}
                                helperText={validationErrors.name}
                                slotProps={{
                                    htmlInput: { maxLength: 100, minLength: 3 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${name.length}/100`}</InputAdornment>,
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
                            <TextField
                                fullWidth
                                label={t('accruals.grants.dialog.fields.description')}
                                value={description}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setDescription(value);
                                    // Validar descripción
                                    if (value) {
                                        const result = GrantFijoCreateSchema.shape.description.safeParse(value);
                                        setValidationErrors((prev) => ({
                                            ...prev,
                                            description: result.success ? undefined : result.error.issues[0]?.message,
                                        }));
                                    } else {
                                        setValidationErrors((prev) => ({
                                            ...prev,
                                            description: undefined,
                                        }));
                                    }
                                }}
                                autoComplete="off"
                                error={!!validationErrors.description}
                                helperText={validationErrors.description}
                                slotProps={{
                                    htmlInput: { maxLength: 255 },
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${description.length}/255`}</InputAdornment>,
                                    },
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <FormControl required fullWidth>
                                <InputLabel>{t('accruals.grants.dialog.fields.grant_type')}</InputLabel>
                                <Select value={grantType} onChange={(e) => setGrantType(e.target.value as GrantTypeEnum)} label={t('accruals.grants.dialog.fields.grant_type')}>
                                    <MenuItem value={GrantTypeEnum.FIJO}>{t('accruals.grants.dialog.fields.fixed')}</MenuItem>
                                    <MenuItem value={GrantTypeEnum.PERCIBIDO}>{t('accruals.grants.dialog.fields.perceived')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            {grantType === GrantTypeEnum.FIJO ? (
                                <GrantFormFijo formData={fijoFormData} setFormData={setFijoFormData} datePatterns={availableDatePatterns} paycodes={availablePaycodes} />
                            ) : (
                                <GrantFormPercibido formData={percibidoFormData} setFormData={setPercibidoFormData} paycodes={availablePaycodes} datePatterns={availableDatePatterns} />
                            )}
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => changes()} variant="outlined" disabled={loading}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button
                    onClick={Create}
                    variant="contained"
                    loading={loading}
                    disabled={loading || !name || !!validationErrors.name || !!validationErrors.description || (grantType === GrantTypeEnum.PERCIBIDO && percibidoFormData.paycodeId === '')}
                >
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
